import { Flex } from 'antd';
import { PrimaryCard } from '../../assets/styles/components/dashboard.styled';
import { Button, Divider, IconButton, Tooltip } from '../Ant';
import CardData from '../dashboard/cardData';
import {
  ArrowRight,
  DocumentNormal,
  Location,
  Mobile,
  Personalicard,
  ProfileUser,
  Sms,
  Star,
  StarFilled,
  User,
} from '../icons';
import { useTranslation } from 'react-i18next';
import { Courthouse } from '../icons/Courthouse';
import { ICustomer } from '../../interface/customer';
import { cardBorderColorMapping, ICustomerType, tagColorMapping } from '../../constants/customer';
import { compact } from 'lodash';
import { displayFullCountryName } from '../../constants/utilityFunctions';
import { useLanguage } from '../../context/LanguageContext';
import { useCreateFavorite } from '../../hooks/useCreateFavorite';
import { useRemoveFromFavorite } from '../../hooks/useRemoveFromFavorite';
import { useNavigationStack } from '../../context/NavigationStackContext';
import { ROUTES } from '../../constants/constants';
import NoteIcons from '../common/NoteIcons';
import SkeletonEntityCard from '../common/skeleton/skeletonEntityCard';
import { getViewLabel } from '../../helpers/viewLabelHelper';

const CustomerDetailView = ({
  details,
  refetch,
  isLoading = false,
}: {
  details: ICustomer;
  refetch: () => void;
  isLoading?: boolean;
}) => {
  const { t } = useTranslation();
  const { push } = useNavigationStack();
  const { getAppLanguageCode } = useLanguage();
  const createFavoriteMutation = useCreateFavorite();
  const removeFavoriteMutation = useRemoveFromFavorite();
  const fullPrimaryAddress = compact([
    details?.primaryAddress?.line1,
    details?.primaryAddress?.line2,
    details?.primaryAddress?.line3,
    details?.primaryAddress?.city,
  ]).join(', ');
  const handleFavouritesToggle = () => {
    const param =
      details?.type === ICustomerType.COMPANY
        ? { companyId: details?.id }
        : { privatePersonId: details?.id };
    if (!details?.isFavorite) {
      createFavoriteMutation.mutate(
        {
          ...param,
          successMessage: t('customer:addedToYourFavorites'),
        },
        {
          onSuccess: () => {
            refetch();
          },
        },
      );
    } else {
      removeFavoriteMutation.mutate(
        {
          ...param,
          successMessage: t('customer:deletedFromYourFavorites'),
        },
        {
          onSuccess: () => {
            refetch();
          },
        },
      );
    }
  };
  const handleGoToCustomer = (id: string) => {
    push(ROUTES.CUSTOMER_LISTING + `/${id}`);
  };
  if (isLoading || !details) {
    return (
      <SkeletonEntityCard
        rows={[1, 1, 2, 2, 1, 1]}
        withSubcontentOnFirstRow
        className="py-10 px-10 h-full"
      />
    );
  }
  return (
    <PrimaryCard
      $isDetail
      className={`d-flex flex-col gap-10 h-full customer-card ${cardBorderColorMapping[details?.type]}`}
    >
      <Flex justify="space-between" align="center" className="w-full">
        <Flex gap={10} align="center">
          <Flex
            justify="center"
            align="center"
            className={`status-chip ${tagColorMapping[details?.type]}`}
            data-testid={`customer-status-chip-${details?.id}`}
          >
            {details?.type === ICustomerType.COMPANY
              ? t('customer:customerType.company')
              : t('customer:customerType.privatePerson')}
          </Flex>
          <NoteIcons hasImportantNotes={details?.hasImportantNotes} hasNotes={details?.hasNotes} />
        </Flex>
        <Tooltip title={t('common:favorite')} placement="top" arrow={false}>
          <IconButton
            data-testid={`favorite-icon-${details?.id}`}
            aria-label={t('common:favorite')}
            aria-pressed={Boolean(details?.isFavorite)}
            className={`favourite-icon ${details?.isFavorite ? 'active' : ''}`}
            onClick={handleFavouritesToggle}
          >
            {details?.isFavorite ? <StarFilled /> : <Star />}
          </IconButton>
        </Tooltip>
      </Flex>
      <Divider />
      <Flex vertical gap={10} className="flex-grow">
        <CardData
          type="customer"
          icon={<ProfileUser />}
          title={t('customer:customer')}
          content={details?.name}
          subcontent={displayFullCountryName(details?.homeCountry, getAppLanguageCode()) ?? ''}
          contentclass="lineclamp-1"
        />

        {details?.type === ICustomerType.PRIVATE_PERSON && (
          <CardData
            type="personalId"
            icon={<Personalicard />}
            title={t('customer:personalId')}
            content={details?.personalId ?? '-'}
          />
        )}

        {details?.type === ICustomerType.COMPANY && (
          <CardData
            type="company"
            icon={<Courthouse />}
            title={t('customer:parentCompany')}
            content={details?.parentCompany?.name ?? '-'}
            headingcontent={
              details?.parentCompany?.name && (
                <Button
                  data-testid="parent-company-button"
                  type="text"
                  icon={<ArrowRight />}
                  iconPosition="end"
                  className="text-cta"
                  onClick={() => handleGoToCustomer(details?.parentCompany?.id)}
                >
                  {getViewLabel(t, 'customer:customer')}
                </Button>
              )
            }
          />
        )}
        {details?.type === ICustomerType.PRIVATE_PERSON && (
          <CardData
            type="privatePersonCompany"
            icon={<Courthouse />}
            title={t('customer:customer')}
            content={details?.privatePersonCompany?.name ?? '-'}
            contentclass="lineclamp-1"
            headingcontent={
              details?.privatePersonCompany?.name && (
                <Button
                  data-testid="private-person-company-button"
                  type="text"
                  icon={<ArrowRight />}
                  iconPosition="end"
                  className="text-cta"
                  onClick={() => handleGoToCustomer(details?.privatePersonCompany?.id)}
                >
                  {getViewLabel(t, 'customer:customer')}
                </Button>
              )
            }
          />
        )}
        <Flex justify="space-between">
          <CardData
            type="phone"
            icon={<Mobile />}
            title={t('customer:phoneNumber')}
            content={
              !details?.primaryPhone || details?.primaryPhone === '' ? '-' : details?.primaryPhone
            }
          />
          <CardData
            type="email"
            icon={<Sms />}
            title={t('customer:email')}
            content={
              details?.email ? (
                <a href={`mailto:${details.email}`} className="conflict-detail-email">
                  {details.email}
                </a>
              ) : (
                '-'
              )
            }
          />
        </Flex>

        {details?.type === ICustomerType.COMPANY && (
          <Flex justify="space-between">
            <CardData
              type="businessId"
              icon={<Personalicard />}
              title={t('customer:businessId')}
              content={details?.businessId ?? '-'}
            />
            <CardData
              type="vatNumber"
              icon={<DocumentNormal />}
              title={t('customer:vatNumber')}
              content={details?.vatNumber ?? '-'}
            />
          </Flex>
        )}
        <CardData
          type="address"
          icon={<Location />}
          title={t('customer:address')}
          content={fullPrimaryAddress?.trim()?.length > 0 ? fullPrimaryAddress : '-'}
          contentclass="lineclamp-2"
        />
        <Divider />
        <CardData
          type="responsibleUser"
          icon={<User />}
          title={t('customer:responsibleUser')}
          content={details?.responsibleUser?.fullName ?? '-'}
        />
      </Flex>
    </PrimaryCard>
  );
};

export default CustomerDetailView;
